import { Button, CircularProgress, Divider, MenuItem } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useIsAuthenticated } from "@azure/msal-react";
import type { ProductType } from "@repo/api";
import {
	AllowedChangesBooleanKey,
	fetchAllowedChanges,
	LocalStorageKeys,
	resetContractOverrideFlag,
	resetContractOverrides,
	selectAllContracts,
	selectError,
	selectIsLoaded,
	selectIsLoading,
	useAppDispatch,
	useAppSelector,
} from "@repo/logic";
import { type FC, useEffect, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import {
	EMPTY_OVERRIDES,
	getApplicableBooleanFlags,
	OVERRIDE_ACTIONS,
} from "./AllowedChangesDevTool.constants";
import type { AllowedChangesEffectiveFlagValues, AllowedChangesFormValues } from "./AllowedChangesDevTool.helpers";
import { buildFlagValues, getEffectiveFlagValue } from "./AllowedChangesDevTool.helpers";
import * as Styled from "./AllowedChangesDevTool.styled";

type AllowedChangesDevToolProps = {
	open: boolean;
	onClose: () => void;
	effectiveFlagValues?: AllowedChangesEffectiveFlagValues;
};

export const AllowedChangesDevTool: FC<AllowedChangesDevToolProps> = ({
	open,
	onClose,
	effectiveFlagValues = EMPTY_OVERRIDES,
}) => {
	const dispatch = useAppDispatch();
	const { t } = useTypedTranslation();
	const isAuthenticated = useIsAuthenticated();
	const contracts = useAppSelector(selectAllContracts);
	const isLoaded = useAppSelector(selectIsLoaded);
	const loading = useAppSelector(selectIsLoading);
	const error = useAppSelector(selectError);
	const methods = useForm<AllowedChangesFormValues>({
		defaultValues: {
			selectedId: "",
			flags: {},
		},
	});
	const { control, handleSubmit, reset, setValue } = methods;
	const selectedId = useWatch({ control, name: "selectedId" }) ?? "";
	const watchedFlags = useWatch({ control, name: "flags" }) ?? {};

	const overrides = useAppSelector((state) =>
		selectedId ? state.allowedChanges.overrides[selectedId] ?? EMPTY_OVERRIDES : EMPTY_OVERRIDES,
	);

	const selectedOriginalContract = useAppSelector((state) =>
		selectedId ? state.allowedChanges.data[selectedId] : undefined,
	);
	const applicableBooleanFlags = useMemo(
		() => getApplicableBooleanFlags(selectedOriginalContract?.productType),
		[selectedOriginalContract?.productType],
	);

	const defaultFlags = useMemo(
		() => buildFlagValues(selectedOriginalContract, EMPTY_OVERRIDES, effectiveFlagValues),
		[effectiveFlagValues, selectedOriginalContract],
	);

	// Load allowed-changes data when the dialog opens and the user is authenticated.
	useEffect(() => {
		if (open && !isLoaded && !loading && isAuthenticated) {
			dispatch(fetchAllowedChanges());
		}
	}, [open, isLoaded, loading, isAuthenticated, dispatch]);

	// Preselect the first available contract once the list is loaded.
	useEffect(() => {
		if (contracts.length > 0 && !selectedId) {
			setValue("selectedId", contracts[0].contractId, { shouldDirty: false });
		}
	}, [contracts, selectedId, setValue]);

	// Reset form values whenever the selected contract or its effective overrides change.
	useEffect(() => {
		if (!selectedId || !selectedOriginalContract) return;

		reset(
			{
				selectedId,
				flags: buildFlagValues(selectedOriginalContract, overrides, effectiveFlagValues),
			},
			{ keepDirty: false },
		);
	}, [effectiveFlagValues, selectedId, selectedOriginalContract, overrides, reset]);

	const getDraftValue = (flag: AllowedChangesBooleanKey): boolean => Boolean(watchedFlags[flag]);
	const getCurrentValue = (flag: AllowedChangesBooleanKey): boolean =>
		overrides[flag] ?? getEffectiveFlagValue(selectedOriginalContract, flag, effectiveFlagValues);

	const isModified = (flag: AllowedChangesBooleanKey): boolean => {
		if (!selectedOriginalContract) return false;
		return getDraftValue(flag) !== getCurrentValue(flag);
	};

	const handleToggle = (flag: AllowedChangesBooleanKey) => {
		setValue(
			"flags",
			{
				...watchedFlags,
				[flag]: !getDraftValue(flag),
			},
			{ shouldDirty: true },
		);
	};

	const handleReset = () => {
		if (!selectedId || !selectedOriginalContract) return;

		dispatch(resetContractOverrides(selectedId));
		reset(
			{
				selectedId,
				flags: buildFlagValues(selectedOriginalContract, EMPTY_OVERRIDES, effectiveFlagValues),
			},
			{ keepDirty: false },
		);
	};

	const handleSetAll = (value: boolean) => {
		setValue(
			"flags",
			applicableBooleanFlags.reduce<Partial<Record<AllowedChangesBooleanKey, boolean>>>((acc, { key }) => {
				acc[key] = value;
				return acc;
			}, {}),
			{ shouldDirty: true },
		);
	};

	const handleSave = (data: AllowedChangesFormValues) => {
		if (!selectedId || !selectedOriginalContract) return;

		const changedItems = applicableBooleanFlags.reduce<Array<{ flag: AllowedChangesBooleanKey; value: boolean; originalValue: boolean }>>((acc, { key }) => {
			const nextValue = Boolean(data.flags[key]);
			const currentValue = getCurrentValue(key);
			const originalValue = getEffectiveFlagValue(selectedOriginalContract, key, effectiveFlagValues);

			if (nextValue !== currentValue) {
				acc.push({ flag: key, value: nextValue, originalValue });
			}

			return acc;
		}, []);

		for (const { flag, value, originalValue } of changedItems) {
			if (value === originalValue) {
				const action = resetContractOverrideFlag({
					contractId: selectedId,
					flag,
				});
				dispatch(action);
				continue;
			}

			const actionCreator = OVERRIDE_ACTIONS[flag as keyof typeof OVERRIDE_ACTIONS];
			if (actionCreator) {
				const action = actionCreator({ contractId: selectedId, value });
				dispatch(action);
			}
		}

		if (
			applicableBooleanFlags.some(({ key }) => key === AllowedChangesBooleanKey.IsBankIdIdentificationRequired) &&
			data.flags.isBankIdIdentificationRequired === true
		) {
			localStorage.removeItem(LocalStorageKeys.LAST_BANK_ID_CHECK);
		}
		if (data.flags.isAmlFulfilled === false) {
			localStorage.removeItem(LocalStorageKeys.LAST_AML_CHECK);
		}

		onClose();
	};

	const overrideCount = selectedOriginalContract
		? applicableBooleanFlags.filter(({ key }) => isModified(key)).length
		: 0;
	const hasDraftChangesFromDefaults = applicableBooleanFlags.some(
		({ key }) => getDraftValue(key) !== Boolean(defaultFlags[key]),
	);
	const areAllFlagsEnabled = applicableBooleanFlags.every(({ key }) => getDraftValue(key));
	const getProductTypeLabel = (productType: ProductType) => t(`profile.changes.productType.${(productType)}`);

	return (
		<FormProvider {...methods}>
			<Styled.StyledDialog open={open} onClose={onClose} maxWidth={false}>
				<Styled.StyledDialogTitle>
					<Styled.DialogTitleRow>
						<Styled.DialogTitleText>Allowed Changes</Styled.DialogTitleText>
						{overrideCount > 0 && (
							<Styled.OverrideCountChip
								label={`${overrideCount} change${overrideCount > 1 ? "s" : ""}`}
								color="warning"
								variant="outlined"
							/>
						)}
					</Styled.DialogTitleRow>
					<Styled.CloseButton onClick={onClose} color="inherit">
						<CloseIcon />
					</Styled.CloseButton>
				</Styled.StyledDialogTitle>

				<Divider />

				<Styled.StyledDialogContent>
					<form
						id="allowed-changes-dev-tool-form"
						onSubmit={(e) => {
							void handleSubmit(handleSave)(e);
						}}
					>
						{loading && (
							<Styled.LoadingState>
								<CircularProgress size={40} />
								<Styled.LoadingText color="text.secondary">Loading contracts...</Styled.LoadingText>
							</Styled.LoadingState>
						)}

						{error && (
							<Styled.ErrorState>
								<Styled.ErrorText color="error">{error}</Styled.ErrorText>
								<Button
									onClick={() => {
										dispatch(fetchAllowedChanges());
									}}
								>
									Retry
								</Button>
							</Styled.ErrorState>
						)}

						{!loading && !error && contracts.length > 0 && (
							<>
								<Styled.SectionLabel color="text.secondary">Select contract</Styled.SectionLabel>
								<Styled.ContractSelect
									value={selectedId}
									onChange={(e) =>
										setValue("selectedId", String(e.target.value), { shouldDirty: false })
									}
									fullWidth
									displayEmpty
									MenuProps={Styled.ContractSelectMenuProps}
									renderValue={(val) => {
										const contract = contracts.find((item) => item.contractId === val);
										if (!contract) return "Select contract...";
										const label = getProductTypeLabel(contract.productType);
										return (
											<Styled.SelectedContractText>
												{contract.contractId}
												<Styled.SelectedContractLabel>
													({label})
												</Styled.SelectedContractLabel>
											</Styled.SelectedContractText>
										);
									}}
								>
									{contracts.map((contract) => (
										<MenuItem key={contract.contractId} value={contract.contractId}>
											<Styled.ContractSelectText>
												{contract.contractId}
												<Styled.ContractSelectLabel>
													({getProductTypeLabel(contract.productType)})
												</Styled.ContractSelectLabel>
											</Styled.ContractSelectText>
										</MenuItem>
									))}
								</Styled.ContractSelect>

								{selectedOriginalContract && (
									<Styled.SelectedContractSection>
										<Styled.ContractListControls>
											<Styled.ToggleAllSwitch
												checked={areAllFlagsEnabled}
												onChange={(_, checked) => handleSetAll(checked)}
											/>
										</Styled.ContractListControls>
										<Styled.FlagsList>
											{applicableBooleanFlags.map(({ key, label }) => {
												const value = getDraftValue(key);
												const modified = isModified(key);

												return (
													<Styled.FlagRow key={key} modified={modified}>
														<Styled.FlagDescription>
															<Styled.FlagLabel modified={modified}>{label}</Styled.FlagLabel>
															<Styled.FlagKey>{key}</Styled.FlagKey>
														</Styled.FlagDescription>
														<Styled.FlagSwitch checked={value} onChange={() => handleToggle(key)} />
													</Styled.FlagRow>
												);
											})}
										</Styled.FlagsList>
									</Styled.SelectedContractSection>
								)}
							</>
						)}

						{!loading && !error && contracts.length === 0 && isLoaded && (
							<Styled.EmptyStateText variant="body2" color="text.secondary">
								No contracts found.
							</Styled.EmptyStateText>
						)}
					</form>
				</Styled.StyledDialogContent>

				<Divider />

				<Styled.StyledDialogActions>
					<Button
						startIcon={<RestartAltIcon />}
						onClick={handleReset}
						size="small"
						color="inherit"
						disabled={!hasDraftChangesFromDefaults}
					>
						Use defaults
					</Button>
					<Button type="submit" form="allowed-changes-dev-tool-form" variant="contained" size="small">
						Save
					</Button>
				</Styled.StyledDialogActions>
			</Styled.StyledDialog>
		</FormProvider>
	);
};